import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

/** Hardcoded fallback when API omits is_unlimited (matches osint-users config). */
const UNLIMITED_SEARCH_LICENSE_ID = '8e9c18b4-cced-431c-b243-ee1216a32fc9';

@Injectable({
  providedIn: 'root',
})
export class UserAccountService {
  baseUrl: string = environment.baseUrl;

  constructor(private http: HttpClient) { }

  getUserProfile(): Observable<any> {
    const url: string = `${this.baseUrl}/auth/me`;
    return this.http.get(url);
  }

  private resolveSubscriptionRecord(responseOrRecord: any): any {
    const data = responseOrRecord?.data ?? responseOrRecord ?? {};
    return (
      data.subscriptions?.[0] ||
      data.currentPlan ||
      data.subscription ||
      data
    );
  }

  /** True while the active subscription is Unlimited Searches. */
  pickIsUnlimited(responseOrRecord: any): boolean {
    const data = responseOrRecord?.data ?? responseOrRecord ?? {};
    const subscription = this.resolveSubscriptionRecord(responseOrRecord);
    if (
      data.is_unlimited === true ||
      data.isUnlimited === true ||
      subscription?.is_unlimited === true ||
      subscription?.isUnlimited === true
    ) {
      return true;
    }
    if (
      subscription?.license_id === UNLIMITED_SEARCH_LICENSE_ID ||
      data.license_id === UNLIMITED_SEARCH_LICENSE_ID
    ) {
      return true;
    }
    const licenseName = String(
      subscription?.license_name || data.license_name || '',
    ).toLowerCase();
    return licenseName.includes('unlimited');
  }

  /**
   * Current + valid queued remaining. Falls back to active-plan remaining.
   * Unlimited returns Infinity so client credit gates never block.
   */
  pickAvailableCredits(responseOrRecord: any): number {
    if (this.pickIsUnlimited(responseOrRecord)) {
      return Number.POSITIVE_INFINITY;
    }
    const data = responseOrRecord?.data ?? responseOrRecord ?? {};
    const subscription = this.resolveSubscriptionRecord(responseOrRecord);
    const value =
      data.available_credits ??
      data.availableCredits ??
      subscription?.available_credits ??
      subscription?.availableCredits ??
      subscription?.remaining_credits ??
      data.remainingCredits ??
      0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /** Display label for remaining credits (infinite vs number). */
  formatCreditsLabel(credits: number | null | undefined): string {
    if (credits != null && !Number.isFinite(Number(credits))) {
      return 'infinite';
    }
    if (credits == null) {
      return '—';
    }
    return String(credits);
  }

  /**
   * Streams help documentation (PDF/video) from /auth/me when help_page=true.
   * Optional scope: osint, leak, advance_leak, face, virtual, vehicle, pan, name, link_shortener.
   */
  getHelpPageAsset(
    scope: string | undefined,
    asset: 'auto' | 'document' | 'video' = 'auto',
  ): Observable<HttpResponse<Blob>> {
    let params = new HttpParams().set('help_page', 'true').set('asset', asset);
    if (scope) {
      params = params.set('scope', scope);
    }
    return this.http.get(`${this.baseUrl}/auth/me`, {
      params,
      responseType: 'blob',
      observe: 'response',
    });
  }

  changePassword(payload: any): Observable<any> {
    const url = `${this.baseUrl}/auth/change-password`;
    return this.http.put(url, payload);
  }

  get2FaCode(): Observable<any> {
    const url = `${this.baseUrl}/auth/2fa/setup`;
    return this.http.get(url);
  }

  verifyEnable2Fa(payload: any): Observable<any> {
    const url = `${this.baseUrl}/auth/2fa/verify-setup`;
    return this.http.put(url, payload);
  }

  disable2FA(payload: any): Observable<any> {
    const url = `${this.baseUrl}/auth/2fa/toggle`;
    return this.http.put(url, payload);
  }

  sendTrackingInfo(payload: any): Observable<any> {
    const url = `${this.baseUrl}/auth/track-login`;
    return this.http.post(url, payload);
  }

  getActivePlans(): Observable<any> {
    const url = `${this.baseUrl}/subscription/plans`;
    return this.http.get(url);
  }

  getSubscriptionDetails(): Observable<any> {
    const url = `${this.baseUrl}/subscription/details`;
    return this.http.get(url);
  }

  expireActiveCredit(): Observable<any> {
    const url = `${this.baseUrl}/subscription/expire-active-credit`;
    return this.http.post(url, {});
  }

  activateCredit(subscriptionId: string): Observable<any> {
    const url = `${this.baseUrl}/subscription/activate-credit`;
    return this.http.post(url, { subscription_id: subscriptionId });
  }

  getBillingDetails(): Observable<any> {
    return this.http.get(`${this.baseUrl}/subscription/billing-details`);
  }

  saveBillingDetails(payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/subscription/billing-details`, payload);
  }

  getPaymentInvoice(
    transactionId: string,
    download: boolean = false,
  ): Observable<HttpResponse<Blob>> {
    const params = new HttpParams().set('download', String(download));
    return this.http.get(
      `${this.baseUrl}/subscription/payments/${transactionId}/invoice`,
      {
        params,
        responseType: 'blob',
        observe: 'response',
      },
    );
  }

  getFilenameFromResponse(
    response: HttpResponse<Blob>,
    fallback: string = 'invoice.pdf',
  ): string {
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    return match?.[1] || fallback;
  }

  saveBlobFile(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  openBlobInNewTab(blob: Blob): void {
    const url = URL.createObjectURL(blob);
    const opened = window.open(url, '_blank');
    if (!opened) {
      this.saveBlobFile(blob, 'invoice.pdf');
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  async parseErrorMessage(
    error: any,
    fallback: string = 'Unable to complete request.',
  ): Promise<string> {
    const body = error?.error;
    if (body instanceof Blob) {
      try {
        const parsed = JSON.parse(await body.text());
        return parsed?.message_key || parsed?.message || fallback;
      } catch {
        return fallback;
      }
    }
    return body?.message_key || body?.message || error?.message || fallback;
  }
}
